import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  DollarSign,
  Eye,
  Loader2,
  MessageSquare,
  ShoppingCart,
  TrendingDown,
  TrendingUp,
} from 'lucide-react';
import { API } from '../App';
import { devError } from '../utils/logger';

const formatCurrency = (n) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(n || 0);

const PIE_COLORS = ['#c9a961', '#5b21b6', '#10b981', '#3b82f6', '#f97316', '#ec4899', '#8b5cf6'];

const DeltaBadge = ({ pct }) => {
  if (pct === null || pct === undefined) {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-muted-foreground/60" title="No data in previous period">
        — vs prev
      </span>
    );
  }
  const positive = pct >= 0;
  const Icon = positive ? TrendingUp : TrendingDown;
  return (
    <span
      className={`inline-flex items-center gap-1 text-xs font-medium ${
        positive ? 'text-emerald-400' : 'text-rose-400'
      }`}
    >
      <Icon className="w-3 h-3" />
      {positive ? '+' : ''}
      {pct}% vs prev
    </span>
  );
};

const StatCard = ({ icon: Icon, label, value, sub, delta, testid }) => (
  <div className="p-5 rounded-lg bg-card/50 border border-border/20" data-testid={testid}>
    <Icon className="w-5 h-5 text-accent mb-3" />
    <p className="text-2xl font-bold text-foreground">{value}</p>
    <p className="text-xs text-muted-foreground mt-1">{label}</p>
    <div className="mt-2 flex items-center justify-between gap-2">
      {delta !== undefined ? <DeltaBadge pct={delta} /> : <span />}
      {sub && <p className="text-xs text-muted-foreground/70 text-right">{sub}</p>}
    </div>
  </div>
);

export default function AnalyticsPanel() {
  const [windowDays, setWindowDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios
      .get(`${API}/supplier/analytics?days=${windowDays}`)
      .then(({ data: payload }) => {
        if (!cancelled) setData(payload);
      })
      .catch((e) => {
        if (cancelled) return;
        devError('analytics load failed', e);
        toast.error('Could not load analytics');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [windowDays]);

  // Trim chart x-axis labels for readability
  const salesData = useMemo(
    () =>
      (data?.sales_series || []).map((s) => ({
        ...s,
        label: s.date.slice(5), // MM-DD
      })),
    [data],
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16" data-testid="analytics-loading">
        <Loader2 className="w-6 h-6 animate-spin text-accent" />
      </div>
    );
  }

  const t = data?.totals || {};
  const deltas = data?.previous_period?.deltas || {};

  return (
    <div className="space-y-8" data-testid="analytics-panel">
      {/* Window selector */}
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-accent mb-1">Performance</p>
          <h2 className="font-playfair text-2xl text-foreground">Last {windowDays} days</h2>
        </div>
        <div className="inline-flex rounded-md border border-border/30 bg-card/40" role="group" data-testid="analytics-window-toggle">
          {[7, 30, 90].map((d) => (
            <button
              key={d}
              onClick={() => setWindowDays(d)}
              data-testid={`analytics-window-${d}`}
              className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                windowDays === d
                  ? 'bg-accent/15 text-accent'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {d}d
            </button>
          ))}
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard
          icon={DollarSign}
          label="Revenue (window)"
          value={formatCurrency(t.revenue_window)}
          delta={deltas.revenue_pct}
          sub={`${t.orders_window || 0} order${t.orders_window === 1 ? '' : 's'}`}
          testid="analytics-revenue-card"
        />
        <StatCard
          icon={ShoppingCart}
          label="Orders (window)"
          value={t.orders_window || 0}
          delta={deltas.orders_pct}
          testid="analytics-orders-card"
        />
        <StatCard
          icon={Eye}
          label="Total views"
          value={t.views || 0}
          sub={`across ${t.listings || 0} listing${t.listings === 1 ? '' : 's'}`}
          testid="analytics-views-card"
        />
        <StatCard
          icon={MessageSquare}
          label="Inquiry / view rate"
          value={`${t.inquiry_per_view_pct ?? 0}%`}
          delta={deltas.inquiries_pct}
          sub={`${t.inquiries_window || 0} inquiries`}
          testid="analytics-conversion-card"
        />
      </div>

      {/* Sales over time */}
      <div className="rounded-xl border border-border/20 bg-card/30 p-4 md:p-6" data-testid="analytics-sales-chart">
        <div className="flex items-center justify-between mb-4">
          <p className="text-sm uppercase tracking-widest text-muted-foreground flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-accent" />
            Revenue over time
          </p>
        </div>
        <div style={{ width: '100%', height: 240 }}>
          <ResponsiveContainer>
            <AreaChart data={salesData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="revenueGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#c9a961" stopOpacity={0.55} />
                  <stop offset="100%" stopColor="#c9a961" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
              <XAxis dataKey="label" stroke="#6b7280" fontSize={11} />
              <YAxis stroke="#6b7280" fontSize={11} tickFormatter={(v) => (v >= 1000 ? `${(v / 1000).toFixed(1)}k` : v)} />
              <Tooltip
                contentStyle={{ background: '#0f1118', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 8 }}
                formatter={(v, n) => (n === 'revenue' ? formatCurrency(v) : v)}
              />
              <Area type="monotone" dataKey="revenue" stroke="#c9a961" strokeWidth={2} fill="url(#revenueGrad)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Two-up: Top viewed + Category breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="rounded-xl border border-border/20 bg-card/30 p-4 md:p-6" data-testid="analytics-top-viewed">
          <p className="text-sm uppercase tracking-widest text-muted-foreground mb-4 flex items-center gap-2">
            <Eye className="w-4 h-4 text-accent" /> Top viewed listings
          </p>
          {(data?.top_viewed || []).length === 0 ? (
            <p className="text-sm text-muted-foreground py-8 text-center">No views yet.</p>
          ) : (
            <div style={{ width: '100%', height: 240 }}>
              <ResponsiveContainer>
                <BarChart
                  data={data.top_viewed}
                  layout="vertical"
                  margin={{ top: 10, right: 10, left: 10, bottom: 0 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                  <XAxis type="number" stroke="#6b7280" fontSize={11} />
                  <YAxis
                    type="category"
                    dataKey="title"
                    stroke="#6b7280"
                    fontSize={11}
                    width={110}
                    tickFormatter={(v) => (v.length > 18 ? `${v.slice(0, 17)}…` : v)}
                  />
                  <Tooltip contentStyle={{ background: '#0f1118', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 8 }} />
                  <Bar dataKey="views" fill="#5b21b6" radius={[0, 6, 6, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        <div className="rounded-xl border border-border/20 bg-card/30 p-4 md:p-6" data-testid="analytics-category-breakdown">
          <p className="text-sm uppercase tracking-widest text-muted-foreground mb-4">Categories</p>
          {(data?.category_breakdown || []).length === 0 ? (
            <p className="text-sm text-muted-foreground py-8 text-center">No listings yet.</p>
          ) : (
            <div style={{ width: '100%', height: 240 }}>
              <ResponsiveContainer>
                <PieChart>
                  <Pie
                    data={data.category_breakdown}
                    dataKey="count"
                    nameKey="category"
                    innerRadius={50}
                    outerRadius={80}
                    paddingAngle={2}
                  >
                    {data.category_breakdown.map((entry, i) => (
                      <Cell key={entry.category} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Legend wrapperStyle={{ fontSize: 12, color: '#9ca3af' }} />
                  <Tooltip contentStyle={{ background: '#0f1118', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 8 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
